import { memo, useCallback, useEffect, useState } from "react";
import { FleetTireAddRow } from "services/api/fleet/tires/types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { TextField } from "ui/components/TextField/TextField";
import { Toggle } from "ui/components/Toggle";
import { getTireDimensionsAPI } from "services/api/fleet/tires/api";

interface TireGroupHeaderProps {
    label: string;
    indices: number[];
    sample: FleetTireAddRow;
    manufacturerOptions: DropdownOption[];
    tireTypeOptions: DropdownOption[];
    onChange: (indices: number[], field: string, value: any) => void;
}

const TireGroupHeader: React.FC<TireGroupHeaderProps> = (props) => {
    const { label, indices, sample, manufacturerOptions, tireTypeOptions, onChange } = props;
    const prefix = `g-${indices[0]}`;

    const [widthOptions, setWidthOptions] = useState<DropdownOption[]>([]);
    const [heightOptions, setHeightOptions] = useState<DropdownOption[]>([]);
    const [radiusOptions, setRadiusOptions] = useState<DropdownOption[]>([]);

    // Dohvati width opcije na mount
    useEffect(() => {
        getTireDimensionsAPI().then((data) => {
            setWidthOptions(data.map((v) => ({ value: v, text: String(v) })));
        }).catch(() => { /* */ });
    }, []);

    // Kad se width promijeni → dohvati heights
    useEffect(() => {
        if (sample.width) {
            getTireDimensionsAPI(sample.width).then((data) => {
                setHeightOptions(data.map((v) => ({ value: v, text: String(v) })));
            }).catch(() => { /* */ });
        } else {
            setHeightOptions([]);
        }
    }, [sample.width]);

    // Kad se height promijeni → dohvati radii
    useEffect(() => {
        if (sample.width && sample.height) {
            getTireDimensionsAPI(sample.width, sample.height).then((data) => {
                setRadiusOptions(data.map((v) => ({ value: v, text: String(v) })));
            }).catch(() => { /* */ });
        } else {
            setRadiusOptions([]);
        }
    }, [sample.width, sample.height]);

    const handleWidthChange = useCallback((_e: any, _n: string, item: DropdownOption) => {
        onChange(indices, "width", item.value);
        onChange(indices, "height", null);
        onChange(indices, "radius", null);
    }, [indices, onChange]);

    const handleHeightChange = useCallback((_e: any, _n: string, item: DropdownOption) => {
        onChange(indices, "height", item.value);
        onChange(indices, "radius", null);
    }, [indices, onChange]);

    return (
        <tr className="border-y border-slate-200 bg-slate-50 text-slate-900">
            <td className="px-4 pt-2 pb-3 text-sm font-semibold text-slate-700" colSpan={2}>
                {label}
                <span className="text-xs font-normal text-slate-400 ml-2">({indices.length} gume)</span>
            </td>
            <td className="px-4 pt-2 pb-2 text-sm font-medium">
                <div className="w-[70px]">
                    <Dropdown name={`${prefix}-w`} options={widthOptions} value={sample.width} onChange={handleWidthChange} height="small" width="full" cssClassName="bg-white" />
                </div>
            </td>
            <td className="px-4 pt-2 pb-2 text-sm font-medium">
                <div className="w-[70px]">
                    <Dropdown name={`${prefix}-h`} options={heightOptions} value={sample.height} onChange={handleHeightChange} height="small" width="full" cssClassName="bg-white" disabled={!sample.width} />
                </div>
            </td>
            <td className="px-4 pt-2 pb-2 text-sm font-medium">
                <div className="w-[70px]">
                    <Dropdown name={`${prefix}-r`} options={radiusOptions} value={sample.radius} onChange={(_e, _n, item) => onChange(indices, "radius", item.value)} height="small" width="full" cssClassName="bg-white" disabled={!sample.height} />
                </div>
            </td>
            <td className="px-4 pt-2 pb-2 text-sm font-medium">
                <div className="w-[130px]">
                    <Dropdown name={`${prefix}-m`} options={manufacturerOptions} value={sample.tireManufacturer?.id ?? null} onChange={(_e, _n, item) => onChange(indices, "tireManufacturerId", item.value)} height="small" width="full" cssClassName="bg-white" />
                </div>
            </td>
            <td className="px-4 pt-2 pb-2 text-sm font-medium">
                <div className="w-[150px]">
                    <Dropdown name={`${prefix}-t`} options={tireTypeOptions} value={sample.tireType?.id ?? null} onChange={(_e, _n, item) => onChange(indices, "tireTypeId", item.value)} height="small" width="full" cssClassName="bg-white" />
                </div>
            </td>
            <td className="px-4 pt-2 pb-2 text-sm font-medium">
                <div className="w-[70px]">
                    <TextField type="number" name={`${prefix}-d`} value={sample.dot} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(indices, "dot", e.target.value ? Number(e.target.value) : null)} height="small" width="full" cssClassName="bg-white" />
                </div>
            </td>
            <td className="px-4 pt-2 pb-2 text-sm font-medium">
                <div className="w-[70px]">
                    <TextField type="number" name={`${prefix}-p`} value={sample.profileDepth} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(indices, "profileDepth", e.target.value ? Number(e.target.value) : null)} height="small" width="full" cssClassName="bg-white" />
                </div>
            </td>
            <td className="px-4 pt-2 pb-2 text-sm font-medium text-center">
                <Toggle id={`${prefix}-rof`} name="rof" value={sample.rof} onChange={(_id, _name, value) => onChange(indices, "rof", value)} />
            </td>
            <td className="px-4 pt-2 pb-3 text-sm font-medium"></td>
        </tr>
    );
};

export default memo(TireGroupHeader);
